import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import useLabels from '../../../../../../../../hooks/useLabels';
import { DocumentInfoModel } from '../../../../../../../../models/ChatTypes';
import Input from '../../../../../../../input/Input';
import Textarea from '../../../../../../../textarea/Textarea';
import FileListActionsModal from '../../../../../../../file-list-actions-modal/FileListActionsModal';
import { HTTPDataService } from '../../../../../../../../services/HTTPDataService';
import EnvConfigHelper from '../../../../../../../../helpers/envConfigHelper';
import { useAuth } from '../../../../../../../../contexts/auth/AuthContext';
import { useQuery, useMutation } from '@tanstack/react-query';
import Loader from '../../../../../../../loader/Loader';
import Alert from '../../../../../../../alert/Alert';
import { Icon } from '../../../../../../../icon/Icon';
import ButtonAsync from '../../../../../../../button/ButtonAsync';
import { Icons } from '../../../../../../../icon/icons/material';
import { IconTheme } from '../../../../../../../icon/Icon.types';
import Switch from '../../../../../../../switch/Switch';
import InputLabel from '../../../../../../../input-label/InputLabel';
import styles from './DocumentEditModal.module.scss';
import { AxiosProgressEvent } from 'axios';
import Modal from '../../../../../../../modal/Modal';
import { isString } from '../../../../../../../../helpers/typeHelpers';
import { groupBy } from '../../../../../../../../helpers/listsHelpers';
import Button, { ButtonThemes } from '../../../../../../../button/Button';
type DocumentEditModel = Pick<DocumentInfoModel, "display" | "id">;

type DocumentEditProps = {
    document?: DocumentInfoModel;
    editState: DocumentEditModel;
    setEditState: Dispatch<SetStateAction<DocumentEditModel>>;
    showTableSearchConfig?: boolean;
};
type EditModalProps = DocumentEditProps & { isOpen: boolean, onClose: () => void; onSubmit: () => Promise<void>; };

const RelativeLoader = () => {
    return <div style={{ minHeight: 20, position: 'absolute', inset: 0 }}>
        <Loader isInline />
    </div>;
};
const DocumentTableSearchModal = (props: EditModalProps & { document: Required<DocumentEditProps>['document']; }) => {
    const { onSubmit, onClose, document, setEditState } = props;
    const { id } = document;

    const [modelState, setModelState] = useState<TableModel>();
    const [configState, setConfigState] = useState<TableConfig>();
    const [suggestionsState, setSuggestionsState] = useState<TableConfig>();

    const { useModel, useGenerateSuggestion, useSaveConfig, useGetIndexProperties } = useTableSearchServiceHooks(id);
    const { data: model, isLoading: isLoadingModel } = useModel();
    const { data: generatedSuggestions, isPending: isGeneratingSuggestions, mutateAsync: generateSuggestions, reset: resetSuggestions } = useGenerateSuggestion(setSuggestionsState);
    const { data: updateModel, isPending: isSavingConfig, mutateAsync: saveConfig } = useSaveConfig();
    const { data: indexProperties } = useGetIndexProperties();


    const disabled = isGeneratingSuggestions || isSavingConfig;

    useEffect(() => { model && setModelState(model); }, [model]);
    useEffect(() => { updateModel && setModelState(updateModel); }, [updateModel]);

    useEffect(() => { modelState && setConfigState(modelState.config); }, [modelState]);
    useEffect(() => { generatedSuggestions && setSuggestionsState(generatedSuggestions); }, [generatedSuggestions]);

    const state = generatedSuggestions || isGeneratingSuggestions ? suggestionsState : configState;
    const setState = generatedSuggestions ? setSuggestionsState : setConfigState;
    useEffect(() => {
        if (!state) return;
        setEditState(s => {
            const updates = { ...s, display: { ...s.display, name: state.name, description: state.description ?? "" } };
            return updates;
        });
    }, [state, setEditState]);

    const submit = useCallback(async () => {
        if (!state) return;
        await saveConfig(state);
        onSubmit();
        onClose();
    }, [state, saveConfig, onSubmit, onClose]);

    const close = useCallback(() => {
        onSubmit();
        onClose();
    }, [onSubmit, onClose]);

    const handleGenerateSuggestions = useCallback(async () => {
        await generateSuggestions();
    }, [generateSuggestions]);

    const labels = useLabels();
    const editLabels = {
        suggestionDescription: "Let AI analyze and describe your data",
        suggestionText: "This will extending the search capabilities by inferring date formats and mapping boolean column values.",
        suggestionCta: "Generate Suggestions & Infer Settings",
        rejectSuggestions: "Discard Suggestions",
        acceptSuggestions: "Accept Suggestions & Update"
    };

    const [colModalState, setColModalState] = useState<{ info?: ColumnInfo, config?: ColumnConfig; }>({});
    const colModal = useMemo(() => <ColInfoModal info={colModalState.info} config={colModalState.config} onClose={() => setColModalState({})} />, [colModalState]);
    return <FileListActionsModal
        isOpen={props.isOpen}
        size={'large'}
        modal={{
            headline: labels.documentChosesIndexingMethod
        }}
        close={{
            isDisabled: disabled,
            label: generatedSuggestions ? editLabels.rejectSuggestions : labels.cancel,
            onClick: (source) => source === 'action' && generatedSuggestions ? resetSuggestions() : close()
        }}
        submit={{
            isDisabled: disabled,
            label: generatedSuggestions ? editLabels.acceptSuggestions : labels.update,
            onClick: submit
        }}
        errorId={''}
    >
        <div style={{ position: 'relative', minHeight: 150 }}>
            {
                (isLoadingModel || isSavingConfig || !modelState) && <RelativeLoader />
            }
            {!isLoadingModel && modelState && <>

                {/* (!model?.config_submitted || !model?.suggestions) && !generatedSuggestions  */}
                {true && <Alert type='info' headline={editLabels.suggestionDescription} description={editLabels.suggestionText}>
                    <ButtonAsync label={editLabels.suggestionCta} icon={<Icon.Base iconName={Icons.wand} title='' theme={IconTheme.inherit} />} onClick={() => handleGenerateSuggestions()} />
                </Alert>}
                <br />
                <div>
                    <div>
                        <Input label={labels.namePlaceholder} value={state?.name ?? ""} onChange={v => setState(s => s ? ({ ...s, name: v }) : s)} isDisabled={disabled} />
                        <br />
                        <Textarea label={labels.descriptionPlaceholder} rows={3} value={state?.description ?? ""} onChange={v => setState(s => s ? ({ ...s, description: v }) : s)} isDisabled={disabled} />
                    </div>
                    <br />
                    {modelState.info.sheets.map(sheetInfo => {
                        const sheetStateIndex = state?.sheets?.findIndex(x => x.index === sheetInfo.index) ?? -1;
                        const sheetState: SheetConfig = sheetStateIndex > -1 ? state!.sheets![sheetStateIndex] : { index: sheetInfo.index, active: false, columns: [] };
                        const sheetConfig = configState?.sheets?.find(x => x.index === sheetInfo.index);

                        const setSheetState = (update: (sheetConfig: SheetConfig) => void) => {
                            const updated = { ...sheetState! };
                            update(updated);

                            setState(prev => {
                                prev!.sheets = prev!.sheets?.map(s =>
                                    s.index === sheetStateIndex ? updated : s
                                ) ?? [];

                                if (!prev!.sheets!.includes(updated)) {
                                    prev!.sheets = insertByIndex(prev!.sheets!, updated);
                                }
                                return { ...prev! };
                            });
                        };
                        const activeColumns = sheetState.columns?.filter(x => x.active) ?? [];
                        const colTypesLimits = indexProperties && sheetState.columns ? groupBy(activeColumns, "type").map(x => ({ type: typeLabel(x.group as string), count: x.items.length, limit: indexProperties.field_limits[x.group!] ?? 0 })).filter(x => x.limit < x.count) : [];
                        return <div className={styles.sheet} key={sheetInfo.index}>
                            {
                                <div>
                                    <div style={{ position: 'sticky', top: 0, zIndex: 1, paddingBottom: 6, background: 'var(--df-color-primary, rgb(255, 255, 255))', boxShadow: '0px 20px 20px var(--df-color-primary, rgb(255, 255, 255))', }}>
                                        <hr style={{ opacity: .3 }} />
                                        <h5 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon.Base iconName={Icons.grid} title='' /><span>{sheetInfo.index + 1}) {sheetInfo.name} ({sheetInfo.row_count} rows)</span></h5>
                                        {
                                            colTypesLimits.length > 0 && <><br /><Alert type='warning' headline='Column Type Limits Exceeded' description='Columns falling outside of the limit will be ignored' children={colTypesLimits.map(x => <div key={x.type}>- <b>{x.type}</b>: {x.count} / limit {x.limit}</div>)} /></>
                                        }
                                    </div>
                                    <br />
                                    <Input label='Name' value={sheetState?.name ?? ""} onChange={v => setSheetState(s => s.name = v)} isDisabled={disabled} />
                                    <br />
                                    <Input label='Description' value={sheetState?.description ?? ""} onChange={v => setSheetState(s => s.description = v)} isDisabled={disabled} />
                                    <br />
                                    <Switch onChange={e => setSheetState(s => s.active = e.target.checked)} label={sheetState.active ? 'Active (sheet included in index)' : 'Inactive (sheet excluded from index)'} isChecked={sheetState.active ?? false} isDisabled={disabled} />
                                    <br />
                                    <InputLabel label='Columns' className={'df-field-label'} isDisabled={disabled} />
                                    <div style={{ display: 'inline-block', scale: '0.8' }}>
                                        <Button theme={ButtonThemes.textPrimary} label='(Toggle all)' onClick={() => setSheetState(s => s.columns?.forEach(c => c.active = !activeColumns.length))} isDisabled={disabled} />
                                    </div>
                                    <table className='df-table density-high'>
                                        <colgroup>
                                            <col style={{ width: '65px' }} />
                                            <col style={{ width: 'auto' }} />
                                            <col style={{ width: '50%' }} />
                                            <col style={{ width: 'auto' }} />
                                            <col style={{ width: '40px' }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>Active</th>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Type</th>
                                                <th>Info</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sheetInfo.columns.map(columnInfo => {
                                                const columnStateIndex = sheetState.columns?.findIndex(x => x.index === columnInfo.index) ?? -1;
                                                const columnState: ColumnConfig = columnStateIndex > -1 ? sheetState.columns![columnStateIndex] : { index: columnInfo.index };
                                                const columnConfig = sheetConfig?.columns?.find(x => x.index === columnInfo.index);
                                                const type = columnState?.type ?? columnConfig?.type ?? "string";

                                                const setColConfig = (update: (colConfig: ColumnConfig) => void) => {
                                                    const updated = { ...columnState! };
                                                    update(updated);
                                                    setSheetState(prev => {
                                                        prev.columns = prev.columns!.map(c =>
                                                            c.index === columnStateIndex ? updated : c
                                                        ) ?? [];

                                                        if (!prev.columns?.includes(updated)) {
                                                            prev.columns = insertByIndex(prev.columns, updated);
                                                        }
                                                        return { ...prev! };
                                                    });
                                                };
                                                return <tr key={columnInfo.index}>
                                                    <td><Switch label=" " isChecked={columnState.active ?? false} onChange={e => setColConfig(c => c.active = e.target.checked)} isDisabled={disabled} /></td>
                                                    <td>({columnInfo.letter}) {columnInfo.name}</td>
                                                    <td><Input value={columnState?.description ?? ""} onChange={v => setColConfig(c => c.description = v)} isDisabled={disabled} /></td>
                                                    <td>{typeLabel(type)}</td>
                                                    <td>
                                                        <Icon.Base iconName={Icons.info} title='' className={styles.icon} onClick={() => setColModalState({ info: columnInfo, config: columnState })} isDisabled={disabled} />
                                                    </td>
                                                </tr>;
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            }
                        </div>;
                    })}
                </div>
            </>}
        </div>
        {colModal}
    </FileListActionsModal>;
};

const ColInfoModal = ({ info, config, onClose }: { info?: ColumnInfo, config?: ColumnConfig; onClose: () => void; }) => {
    if (!info) return null;
    const valueSamples = config?.value_sample ?? info?.value_sample;
    return <Modal
        isOpen={!!info}
        onClose={onClose}
        headline="Column Info"
        body={
            <div className={styles['column-info-modal']}>
                <table className='df-table density-high'>
                    <colgroup>
                        <col style={{ width: '150px' }} />
                        <col style={{ width: 'auto' }} />
                    </colgroup>
                    <tbody>
                        <tr>
                            <td>Index</td>
                            <td>{info.index}</td>
                        </tr>
                        <tr>
                            <td>Letter</td>
                            <td>{info.letter}</td>
                        </tr>
                        <tr>
                            <td>Name</td>
                            <td>{info.name}</td>
                        </tr>
                        <tr>
                            <td>Description</td>
                            <td>{config?.description}</td>
                        </tr>
                        <tr>
                            <td>Type</td>
                            <td>{typeLabel(config?.type ?? info.type)}</td>
                        </tr>
                        <tr>
                            <td>Max Length</td>
                            <td>{info.max_length ?? 'N/A'}</td>
                        </tr>
                        <tr>
                            <td>Avg Length</td>
                            <td>{info.avg_length ?? 'N/A'}</td>
                        </tr>
                        <tr>
                            <td>Value Count</td>
                            <td>{info.value_count}</td>
                        </tr>
                        <tr>
                            <td>Numeric values parser</td>
                            <td>
                                {
                                    (config?.type ?? info.type) !== 'number' || !config?.numeric_tidy_up ? <>N/A</> :
                                        <ul>
                                            <li>Tidy up: {(config?.numeric_tidy_up?.enabled ?? false).toString()}</li>
                                            <li>Decimal separator: {numericSeparatorLabels(config?.numeric_tidy_up?.decimal_separator)}</li>
                                            <li>Thousands separator: {numericSeparatorLabels(config?.numeric_tidy_up?.thousands_separator)}</li>
                                        </ul>
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>Boolean Mapping</td>
                            <td>
                                {config?.bool_mapping ? (
                                    <ul style={{ listStyle: 'disc' }}>
                                        {Object.entries(config.bool_mapping).map(([key, value]) => (
                                            <li key={key}>{key} {'=>'} {value?.toString() ?? 'n/a'}</li>
                                        ))}
                                    </ul>
                                ) : 'N/A'}
                            </td>
                        </tr>
                        <tr>
                            <td>Date/Time Format</td>
                            <td>
                                {config?.datetime_format ? (
                                    <span>{config.datetime_format}</span>
                                ) : 'N/A'}
                            </td>
                        </tr>
                        <tr>
                            <td>Sample Values</td>
                            <td>
                                {!valueSamples?.length ? 'N/A' :
                                    <ul style={{ listStyle: 'disc' }}>
                                        {valueSamples.slice(0, 10).map((value, index) => (
                                            <li key={index}>{`${value}`}</li>
                                        ))}
                                    </ul>
                                }
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        }
    />;
};
const DocumentEditModal = (props: EditModalProps) => {
    const { editState, setEditState } = props;

    const labels = useLabels();

    return <FileListActionsModal
        isOpen={props.isOpen}
        size={'medium'}
        modal={{
            headline: labels.documentUpdateHeader
        }}
        close={{
            isDisabled: true,
            label: labels.cancel,
            onClick: props.onClose
        }}
        submit={{
            isDisabled: !editState.display.name?.length,
            label: labels.update,
            onClick: async () => {
                await props.onSubmit();
                props.onClose();
            }
        }}
        errorId={''}
    >
        <div>
            <Input label={labels.namePlaceholder} value={editState.display.name ?? ''} onChange={v => setEditState(s => ({ ...s, display: { ...s.display, name: v } }))} />

            <Textarea label={labels.descriptionPlaceholder} rows={2} value={editState.display.description ?? ''} onChange={v => setEditState(s => ({ ...s, display: { ...s.display, description: v } }))} />
        </div>
    </FileListActionsModal>;
};

const processMethods = ['table', 'semantic'] as const;
type ProcessingMethod = typeof processMethods[number];
const DocumentProcessingMethodsModal = (props: EditModalProps) => {
    const { useCreateModel, useStartSemanticProcessing } = useTableSearchServiceHooks(props.document?.id ?? "");
    const { data: model, isPending: isCreatingModel, mutateAsync: createModel } = useCreateModel();
    const { isPending: isStartingSemanticProcessing, mutateAsync: startSemanticProcessing } = useStartSemanticProcessing();
    const [selectedMethod, setSelectedMethod] = useState<ProcessingMethod | undefined>('table');
    const isSemanticSearch = selectedMethod === 'semantic';
    const isTableSearch = selectedMethod === 'table';
    const isLoading = isCreatingModel || isStartingSemanticProcessing;
    const labels = useLabels();

    if (model) {
        return <DocumentTableSearchModal {...props} document={props.document!} />;
    }

    return <FileListActionsModal
        isOpen={props.isOpen}
        size={'medium'}
        modal={{
            headline: labels.documentChosesIndexingMethod
        }}
        close={{
            label: labels.cancel,
            onClick: props.onClose
        }}
        submit={{
            isDisabled: !selectedMethod || isLoading,
            label: !selectedMethod ? 'Select Method' : isTableSearch ? 'Configure Table Search' : 'Use Semantic Search',
            onClick: async () => {
                if (isTableSearch) {
                    await createModel();
                }
                if (isSemanticSearch) {
                    await startSemanticProcessing();
                    props.onClose();
                    props.onSubmit();
                }
            }
        }}
        errorId={''}
    >
        <div style={{ position: 'relative', minHeight: 150 }}>
            {
                isLoading && <RelativeLoader />
            }
            <div>
                <h3 className={styles.switch} onClick={() => setSelectedMethod('table')}>
                    <Icon.Base iconName={isTableSearch ? Icons.radio : Icons.emptyRadio} title='' />
                    <Icon.Base iconName={Icons.grid} title='' />
                    <span>Table Search</span>
                </h3>
                <hr />
                <p>
                    <strong>Table Search</strong> is a powerful indexing method designed for well-structured data in tabular formats, such as csv or Excel (with support for multiple sheets)
                    <br />This method enables the model to retrieve data from the document with high accuracy, allowing you to perform advanced queries, filtering, and sorting based on column values.
                </p>
            </div>
            <div>
                <h3 className={styles.switch} onClick={() => setSelectedMethod('semantic')}>
                    <Icon.Base iconName={isSemanticSearch ? Icons.radio : Icons.emptyRadio} title='' />
                    <span>Semantic Search</span>
                </h3>
                <hr />
                <p>
                    <strong>Semantic Search</strong> is an indexing method that uses natural language processing to understand the content of the document.
                    <br />This method is suitable for documents with textual content, such as articles, reports, case studies, and other narrative formats.
                </p>
            </div>
            {
                isTableSearch &&
                <div>
                    <br />
                    <Alert type='warning' headline='Important Note' children={<>Table Search requires the document must be <b>well-structured</b> with the <b>header</b> in the <b>first row</b>, no merged cells, and unique column headers.</>} />
                </div>
            }
            {
                isSemanticSearch &&
                <div>
                    <br />
                    <Alert type='warning' headline='Important Note' children={<>Semantic search is designed for files with textual content and is not suitable for accurate data retrieval.</>} />
                </div>
            }
        </div>
    </FileListActionsModal>;
};
const EditModal = (props: EditModalProps) => {
    const { showTableSearchConfig, document: { is_tabular, state: { table_search_configured, status } = {} } = {} } = props;
    const isTableSearchConfigModel = is_tabular && showTableSearchConfig;
    const showProcessingOptions = isTableSearchConfigModel && !table_search_configured && status === 'uploaded';

    if (!props.isOpen) {
        return;
    }
    if (showProcessingOptions) {
        return <DocumentProcessingMethodsModal {...props} />;
    }
    return isTableSearchConfigModel ? <DocumentTableSearchModal {...props} document={props.document!} /> : <DocumentEditModal {...props} />;
};

export default EditModal;



const useTableSearchServiceHooks = (documentId: string) => {
    const { getAccessToken } = useAuth();
    const service = useMemo(() => new TableSearchService(getAccessToken), [getAccessToken]);
    const endpoints = useMemo(() => {
        const queryBaseKey = ['table-search-model', documentId];
        return {
            useStartSemanticProcessing: () => useMutation({
                mutationKey: [queryBaseKey, 'process-start'],
                mutationFn: () => service.startSemanticProcessing(documentId),
            }),
            useModel: () => useQuery({
                queryKey: [queryBaseKey, 'model-get'],
                queryFn: () => service.getModel(documentId),
                staleTime: 0
            }),
            useCreateModel: () => useMutation({
                mutationKey: [queryBaseKey, 'model-create'],
                mutationFn: () => service.getModel(documentId),
            }),
            useGenerateSuggestion: (handleStream?: (received: TableConfig) => void) => useMutation({
                mutationKey: [queryBaseKey, 'config-generate'],
                mutationFn: () => service.generateSuggestion(documentId, handleStream)
            }),
            useSaveConfig: () => useMutation({
                mutationKey: [queryBaseKey, 'config-save'],
                mutationFn: (config: TableConfig) => service.saveConfig(documentId, config)
            }),
            useGetIndexProperties: () => useQuery({
                queryKey: ['table-search-index-properties'],
                queryFn: () => service.getIndexProperties(),
                staleTime: Infinity
            })
        };
    }, [service, documentId]);

    return endpoints;
};

class TableSearchService extends HTTPDataService {
    baseURL = EnvConfigHelper.get('api-base-url', '');

    constructor(public getAccessToken: () => Promise<string | undefined>) {
        super();
    }
    startSemanticProcessing = (documentId: string) => {
        return this.post<TableModel>(`api/me/document/${documentId}/start-processing`);
    };
    getModel = (documentId: string) => {
        return this.post<TableModel>(`api/me/document/${documentId}/table-search`, { refresh: 'false' });
    };
    generateSuggestion = async (documentId: string, handleStream?: (received: TableConfig) => void) => {
        const parse = (event: AxiosProgressEvent) => {
            if (!handleStream)
                return;

            const text = event.event?.target?.responseText;
            if (!text)
                return;

            const closed = closeJsonFragment(text);
            try {
                const data = JSON.parse(closed) as TableConfig;
                handleStream(data);
            }
            catch (e) {
            }
        };
        const endpoint = `api/me/document/${documentId}/table-search/config/generate`;
        let result = await this.post<TableConfig>(`${endpoint}/stream`, { refresh: 'true' }, undefined, undefined, parse);

        if (isString(result)) {
            // During streaming, the object we get might have serialization issues which manifest in the axios not being able to parse the result into json, so we will refetch the final object once the stream is done.
            result = await this.post<TableConfig>(`${endpoint}`, { refresh: 'false' }, undefined, undefined, parse);
        }

        return result;
    };
    saveConfig = (documentId: string, config: TableConfig) => {
        return this.post<TableModel>(`api/me/document/${documentId}/table-search/config`, {}, config);
    };
    getIndexProperties = () => {
        return this.get<IndexProperties>(`api/me/document/table-search/index-properties`);
    };
}

const typeLabel = (type: string) => {
    if (type === 'string') return 'text';
    return type;
};
const numericSeparatorLabels = (separator?: string) => {
    if (!separator) return 'N/A';
    if (separator === '.') return '. (dot)';
    if (separator === ',') return ', (comma)';
    return separator;
};
export interface IndexProperties {
    field_limits: Record<string, number>;
}
export interface ColumnInfo {
    index: number;
    letter: string;
    name: string;
    type: string;
    max_length: number | null;
    avg_length: number | null;
    value_count: number;
    value_sample?: string[];
}

export interface SheetInfo {
    index: number;
    columns: ColumnInfo[];
    name: string;
    header_row: number;
    row_count: number;
}

export interface TableInfo {
    sheets: SheetInfo[];
    name: string;
    format: string;
    size_bytes: number;
    delimiter: string;
}

export interface ColumnConfig {
    index: number;
    type?: "string" | "date" | "number" | "bool";
    name?: string;
    description?: string;
    active?: boolean;
    datetime_format?: string | null;
    bool_mapping?: Record<string, boolean | undefined>;
    numeric_tidy_up?: {
        enabled?: boolean;
        decimal_separator?: string;
        thousands_separator?: string;
    };
    value_sample?: string[];
}

export interface SheetConfig {
    index: number;
    columns?: ColumnConfig[];
    name?: string;
    description?: string;
    active?: boolean;
}

export interface TableConfig {
    sheets?: SheetConfig[];
    name?: string;
    description?: string;
}

export interface TableModel {
    info: TableInfo;
    config: TableConfig;
    suggestions: TableConfig;
    config_submitted?: boolean;
}

/**
* Inserts an item into an array maintaining sorted order by the `index` property.
* Mutates the original array.
* 
* @param list - Array to insert into
* @param item - Item to insert (must have `index` property)
* @returns The mutated array
*/
function insertByIndex<T extends { index: number; }>(
    list: T[],
    item: T
): T[] {
    // Find insertion point to maintain sorted order by index
    const insertionIndex = list.findIndex(existing => existing.index > item.index);

    if (insertionIndex === -1) {
        // No item with higher index found, append to end
        list.push(item);
    } else {
        // Insert at the found position
        list.splice(insertionIndex, 0, item);
    }

    return list;
}


/**
 * Closes incomplete JSON fragments to make them valid and parseable.
 * Perfect for streaming JSON data from LLMs, APIs, or any source that delivers JSON in fragments.
 *
 * @param fragment Incomplete JSON string from streaming source
 * @returns Complete, valid JSON string ready for JSON.parse()
 *
 * Examples:
 *   closeJsonFragment('{"name": "John')       // '{"name": "John"}'
 *   closeJsonFragment('{"')                  // '{}'
 *   closeJsonFragment('{"people":[{"first_na') // '{"people":[{"first_na": null}]}'
 *   closeJsonFragment('[1, 2, 3')            // '[1, 2, 3]'
 */

export function closeJsonFragment(fragment: string): string {
    if (!fragment || !fragment.trim()) {
        return fragment;
    }

    let stack: string[] = [];
    let inString = false;
    let i = 0;
    let lastNonWhitespaceChar = '';
    let lastNonWhitespaceIndex = -1;

    while (i < fragment.length) {
        const c = fragment[i];

        if (inString) {
            if (c === '"') {
                // Count consecutive backslashes before this quote
                let backslashCount = 0;
                let j = i - 1;
                while (j >= 0 && fragment[j] === '\\') {
                    backslashCount++;
                    j--;
                }
                if (backslashCount % 2 === 0) {
                    inString = false;
                }
            }
        } else {
            if (c === '"') {
                inString = true;
            } else if (c === '{') {
                stack.push('}');
            } else if (c === '[') {
                stack.push(']');
            } else if (c === '}') {
                if (stack.length && stack[stack.length - 1] === '}') {
                    stack.pop();
                }
            } else if (c === ']') {
                if (stack.length && stack[stack.length - 1] === ']') {
                    stack.pop();
                }
            }
            if (!/\s/.test(c)) {
                lastNonWhitespaceChar = c;
                lastNonWhitespaceIndex = i;
            }
        }
        i++;
    }

    // Handle edge case: '{"' => '{}'
    if (
        inString &&
        lastNonWhitespaceChar === '"' &&
        lastNonWhitespaceIndex === fragment.length - 1
    ) {
        const beforeQuote = fragment.substring(0, lastNonWhitespaceIndex).trim();
        if (beforeQuote.endsWith('{') || beforeQuote.endsWith(',')) {
            inString = false;
            fragment = fragment.substring(0, lastNonWhitespaceIndex).replace(/\s*$/, '');
            if (fragment) {
                const stripped = fragment.replace(/\s*$/, '');
                lastNonWhitespaceChar = stripped[stripped.length - 1] || '';
            }
        }
    }

    // Handle incomplete property names
    if (stack.length && stack[stack.length - 1] === '}') {
        if (inString) {
            // Case 1: Incomplete property name, e.g., '{"first_na'
            let openQuotePos = -1;
            for (let j = fragment.length - 1; j >= 0; j--) {
                if (fragment[j] === '"') {
                    // Count backslashes before quote
                    let backslashCount = 0;
                    let k = j - 1;
                    while (k >= 0 && fragment[k] === '\\') {
                        backslashCount++;
                        k--;
                    }
                    if (backslashCount % 2 === 0) {
                        openQuotePos = j;
                        break;
                    }
                }
            }
            if (openQuotePos >= 0) {
                const beforeOpenQuote = fragment.substring(0, openQuotePos).trim();
                if (beforeOpenQuote.endsWith('{') || beforeOpenQuote.endsWith(',')) {
                    fragment += '": null';
                    inString = false;
                }
            }
        } else {
            // Case 2: property name without colon
            const quotedStringMatch = fragment.match(/"([^"\\]|\\.)*"\s*$/);
            if (quotedStringMatch) {
                const stringWithSpaces = quotedStringMatch[0];
                const stringStartPos = fragment.length - stringWithSpaces.length;
                const beforeString = fragment.substring(0, stringStartPos).trim();

                if (
                    (beforeString.endsWith('{') || beforeString.endsWith(',')) &&
                    !stringWithSpaces.includes(':')
                ) {
                    fragment += ': null';
                }
            }
        }
    }

    // If we're still inside a string, close it properly
    if (inString) {
        // Handle trailing backslash: need to escape the closing quote
        let backslashCount = 0;
        let j = fragment.length - 1;
        while (j >= 0 && fragment[j] === '\\') {
            backslashCount++;
            j--;
        }
        if (backslashCount % 2 === 1) {
            fragment += '\\';
        }
        fragment += '"';
    }

    // Handle object keys with missing value after colon
    if (!inString && stack.length && stack[stack.length - 1] === '}') {
        if (lastNonWhitespaceChar === ':') {
            fragment += ' null';
        }
    }

    // Complete partial primitive values (true, false, null)
    if (!inString) {
        const match = fragment.match(/([a-zA-Z]*)$/);
        if (match) {
            const lastWord = match[1];
            for (const keyword of ['true', 'false', 'null']) {
                if (lastWord && keyword.startsWith(lastWord)) {
                    fragment += keyword.slice(lastWord.length);
                    break;
                }
            }
        }
    }

    // Remove trailing commas
    fragment = fragment.replace(/,(\s*)([\]}])/g, '$1$2');
    fragment = fragment.replace(/,\s*$/, '');

    // Close all open structures
    while (stack.length) {
        fragment += stack.pop();
    }

    return fragment;
}

// TODO: Move to a test file
// function runComprehensiveTests() {
//     const testCategories = [
//         {
//             name: "Property Name",
//             tests: [
//                 { input: '{"people":[{"first_na', expected: '{"people":[{"first_na": null}]}', desc: "Incomplete property name (in string)" },
//                 { input: '{"people":[{"first_name"', expected: '{"people":[{"first_name": null}]}', desc: "Complete property name without colon" },
//                 { input: '{"people":[{"first_name": "John", "last_na', expected: '{"people":[{"first_name": "John", "last_na": null}]}', desc: "Incomplete property after comma" },
//                 { input: '{"name": "incomplete_value', expected: '{"name": "incomplete_value"}', desc: "Incomplete property value (should stay as value)" },
//                 { input: '{"obj": {"nested_prop', expected: '{"obj": {"nested_prop": null}}', desc: "Nested incomplete property name" }
//             ]
//         },
//         {
//             name: "Edge Cases",
//             tests: [
//                 { input: '', expected: '', desc: "Empty string input" },
//                 { input: ' ', expected: ' ', desc: "Whitespace only" },
//                 { input: '{', expected: '{}', desc: "Single opening brace" },
//                 { input: '{"', expected: '{}', desc: "Object with opening quote only" },
//                 { input: '{ "', expected: '{}', desc: "Object with spaced opening quote" },
//                 { input: '[', expected: '[]', desc: "Single opening bracket" },
//                 { input: '["', expected: '[""]', desc: "Array with opening quote" },
//                 { input: '{"', expected: '{}', desc: "Object with opening quote only" },
//                 { input: '{ "', expected: '{}', desc: "Object with spaced opening quote" },
//                 { input: '[{"', expected: '[{}]', desc: "Array with incomplete object" },
//                 { input: '["', expected: '[""]', desc: "Array with incomplete string (should complete string)" }
//             ]
//         },
//         {
//             name: "Basic Primitives",
//             tests: [
//                 { input: '"hello', expected: '"hello"', desc: "Simple incomplete string" },
//                 { input: 'tr', expected: 'true', desc: "Incomplete boolean true" },
//                 { input: 'fal', expected: 'false', desc: "Incomplete boolean false" },
//                 { input: 'nul', expected: 'null', desc: "Incomplete null" },
//                 { input: '123', expected: '123', desc: "Complete number (no change)" }
//             ]
//         },
//         {
//             name: "Objects and Arrays",
//             tests: [
//                 { input: '{}', expected: '{}', desc: "Complete empty object" },
//                 { input: '{"key"', expected: '{"key": null}', desc: "Object with incomplete key" },
//                 { input: '{"key":', expected: '{"key": null}', desc: "Object with key and colon" },
//                 { input: '[]', expected: '[]', desc: "Complete empty array" },
//                 { input: '[1,', expected: '[1]', desc: "Array with trailing comma" },
//                 { input: '["str', expected: '["str"]', desc: "Array with incomplete string" }
//             ]
//         },
//         {
//             name: "LLM Streaming Scenarios",
//             tests: [
//                 { input: '{"response": {"text": "Hello world', expected: '{"response": {"text": "Hello world"}}', desc: "LLM response with incomplete text" },
//                 { input: '{"choices": [{"message": {"role": "assistant", "content": "Hi', expected: '{"choices": [{"message": {"role": "assistant", "content": "Hi"}}]}', desc: "OpenAI-style streaming response" }
//             ]
//         }
//     ];

//     let total = 0, passed = 0;
//     for (const cat of testCategories) {
//         console.log(`\nCategory: ${cat.name}`);
//         for (const [i, t] of cat.tests.entries()) {
//             total++;
//             const out = closeJsonFragment(t.input);
//             let ok = out === t.expected;
//             let parsable = true;
//             try { if (out.trim()) JSON.parse(out); } catch { parsable = false; }
//             if (ok && parsable) {
//                 passed++;
//                 console.log(`  ✅ ${i + 1}. ${t.desc}`);
//             } else {
//                 console.log(`  ❌ ${i + 1}. ${t.desc}`);
//                 console.log(`     Input:    '${t.input}'`);
//                 console.log(`     Expected: '${t.expected}'`);
//                 console.log(`     Got:      '${out}'`);
//             }
//         }
//     }
//     console.log(`\nSummary: Passed ${passed} of ${total} (${Math.round(100 * passed / total)}%)`);
// }

// runComprehensiveTests();
